import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/** A lifecycle level. Rows live in `csq_levels`; achievements are written only by the database. */
export interface CsqLevel {
  id: string;
  code: string;
  name: string;
  description: string;
  trigger_type: "event" | "page";
  trigger_key: string | null;
  page_pattern: string | null;
  send_to_csq: boolean;
  csq_path: string;
  csq_goal_id: string | null;
  verified: boolean;
  is_enabled: boolean;
  sort_order: number;
}

/** Events the database can confirm. Keys must match csq_level_trigger() / voice-proxy. */
export const LEVEL_EVENTS: { key: string; label: string; browser: boolean }[] = [
  { key: "account_created", label: "Account created", browser: true },
  { key: "free_plan_active", label: "Free plan active", browser: true },
  { key: "paid_plan_active", label: "Paid plan active (incl. overrides)", browser: true },
  { key: "show_created", label: "Show saved", browser: true },
  { key: "episode_created", label: "Episode or draft saved", browser: true },
  { key: "episode_delivered", label: "Delivered to all assigned platforms", browser: false },
  { key: "clip_generated", label: "Clip generated", browser: true },
  { key: "voice_clone_created", label: "Voice clone created", browser: true },
  { key: "payout_connected", label: "Payout method connected", browser: true },
  { key: "withdrawal_completed", label: "Withdrawal completed", browser: false },
];

export const eventLabel = (key: string | null) => LEVEL_EVENTS.find((e) => e.key === key)?.label ?? key ?? "—";

/** ContentSquare path for a new level code, e.g. "6A" + "Sent a newsletter" → /__csq/milestones/l6a_sent_a_newsletter. */
export function csqPathFor(code: string, name: string): string {
  const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_|_$/g, "");
  return `/__csq/milestones/l${slug(code)}_${slug(name)}`.slice(0, 120);
}

/** Goal name shown in ContentSquare: PENDING until an admin verifies it. */
export const goalName = (l: Pick<CsqLevel, "code" | "name" | "verified">) =>
  `${l.verified ? "" : "PENDING "}L${l.code} | ${l.name}`;

export const CSQ_LEVELS_KEY = ["csq-levels"] as const;

export function useCsqLevels() {
  return useQuery({
    queryKey: CSQ_LEVELS_KEY,
    queryFn: async () => {
      const { data, error } = await supabase.from("csq_levels" as never).select("*").order("sort_order");
      if (error) throw error;
      return (data ?? []) as unknown as CsqLevel[];
    },
    staleTime: 5 * 60 * 1000,
  });
}

export interface LevelAchievement {
  id: string;
  user_id: string;
  level_id: string;
  achieved_at: string;
  source: string;
  reported: boolean;
}
